import { useState, useEffect, useCallback } from 'react';
import { Button, Text, Theme } from '@radix-ui/themes';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { useChangelog } from './useChangelog';
import styles from './ChangelogButton.module.css';

export const ChangelogButton = ({ page }) => {
  const {
    hasUnreadReleases,
    latestTitle,
    latestBody,
    latestCtaUrl,
    latestCtaLabel,
    changelogUrl,
    markAsRead,
  } = useChangelog(page);

  const forceOpen =
    new URLSearchParams(window.location.search).get('changelog') === 'true';

  const [isOpen, setIsOpen] = useState(forceOpen);

  useEffect(() => {
    if (hasUnreadReleases) {
      setIsOpen(true);
    }
  }, [hasUnreadReleases]);

  const dismissModal = useCallback(() => {
    markAsRead();
    setIsOpen(false);
  }, [markAsRead]);

  const handleViewChangelog = useCallback(() => {
    dismissModal();
    const url = latestCtaUrl || changelogUrl;
    try {
      const parsed = new URL(url, window.location.origin);
      if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch {
      // invalid URL, do nothing
    }
  }, [dismissModal, latestCtaUrl, changelogUrl]);

  if (!forceOpen && (!hasUnreadReleases || !isOpen)) {
    return null;
  }

  return (
    <Theme>
      <div className={styles.modalContent} role="status">
        <div className={styles.gradientBorder} />

        <div className={styles.innerContent}>
          <Text as="h2" size="4" weight="bold" className={styles.title}>
            {latestTitle || 'New Features Available'}
          </Text>

          <div className={styles.description}>
            <Markdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw, rehypeSanitize]}
              components={{
                a: ({ children, ...props }) => (
                  <a {...props} target="_blank" rel="noopener noreferrer">
                    {children}
                  </a>
                ),
              }}
            >
              {latestBody ||
                "We've made some exciting updates to improve your experience."}
            </Markdown>
          </div>

          <Button
            size="3"
            onClick={handleViewChangelog}
            style={{ width: '100%' }}
          >
            {latestCtaLabel}
          </Button>

          <button
            className={styles.closeButton}
            aria-label="Close"
            onClick={dismissModal}
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
      </div>
    </Theme>
  );
};

export default ChangelogButton;
